import {
  CompanyQuestionnaireDataInterface,
  CreateUserAssessment,
  QuestionnaireInterface,
  UserResponse,
  assessmentQuestionnaireApiHandler,
} from '@api/assessmentQuestionnaireApiHandler';
import { useUserActionsHooks } from '@api/userActionsApiHandler';
import Modal from '@components/Modal';
import { QuestionnaireType } from '@enums/QuestionnaireTypes';
import { useAppDispatch } from '@hooks/useAppDispatch';
import { useAppSelector } from '@hooks/useAppSelector';
import { ApiResponse } from '@interfaces/index';
import { showToast } from '@shared/atoms';
import { CheckInContent } from '@shared/molecules/CheckInContent/CheckInContent';
import { HelpAndSpeakUp } from '@shared/organisms/EWS/HelpAndSpeakUp/HelpAndSpeakUp';
import { useMutation, useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { ToastMessages } from 'constants/toastMessages';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RootState } from 'store';
import {
  resetQuestionnaire,
  setChosenQuestionnaire,
  setCreatedUserAssessment,
} from 'store/Slices/QuestionnaireSliceV2';
import { DEFAULT_CHECK_IN_VALUE } from '../SingleQuestionnaire/config';
import styles from './index.module.scss';

export const CheckIn = ({
  handleStep,
  modalOpen,
  handleModalOpen,
  refetchCheckInAssessments,
}: CheckInProps) => {
  const dispatch = useAppDispatch();
  const formRef = useRef<HTMLFormElement>(null);
  const [announcement, setAnnouncement] = useState<string>('');
  const company = localStorage.getItem('company');
  const parsedCompany = JSON.parse(company || '{}');
  const popupDataStr = localStorage.getItem('shortStarCacheHistory');
  const [ratings, setRatings] = useState<Record<number, number>>({});
  const companyConfig = useAppSelector((state: RootState) => state.companyConfig.data);
  const { showSpeakUp, showInstantHelp } = companyConfig;
  const [speakUpVisible, setSpeakUpVisible] = useState(false);
  const [helpAndSpeakUpModalOpen, setHelpAndSpeakUpModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [changeDetected, setChangedDetected] = useState<boolean>(false);
  const [showExperts, setShowExperts] = useState(false);
  const updateUserActionMutation = useUserActionsHooks.useUpdateUserAction();

  const fetchQuestionnaires = async (): Promise<ApiResponse<CompanyQuestionnaireDataInterface[]>> =>
    assessmentQuestionnaireApiHandler.getAllCompanyQuestionnaires(parsedCompany.id);

  const fetchQuestionsByQuestionnaire = async (): Promise<
    ApiResponse<QuestionnaireInterface> | undefined
  > => {
    if (!checkInQuestionnaire || !checkInQuestionnaire?.questionnaireId) {
      showToast(ToastMessages.checkIn.error.QUESTIONNAIRE_NOT_FOUND, undefined, 'error');

      return undefined;
    }

    return assessmentQuestionnaireApiHandler.getQuestionsByQuestionnaire(
      checkInQuestionnaire?.questionnaireId,
    );
  };

  const {
    data: questionnairesData,
    isLoading: questionnairesLoading,
    error: questionnairesError,
  } = useQuery({
    queryKey: ['getAllCompanyQuestionnaires', parsedCompany.id],
    queryFn: fetchQuestionnaires,
    enabled: !!parsedCompany?.id,
  });

  const checkInQuestionnaire = useMemo(() => {
    if (!questionnairesData?.success) return null;

    const checkIn =
      questionnairesData.responseObject.find(
        (cq) => cq?.questionnaireV2?.questionnaireType === QuestionnaireType.CHECK_IN,
      ) || null;

    dispatch(setChosenQuestionnaire(checkIn));

    return checkIn;
  }, [questionnairesData, dispatch]);

  const {
    data: questionsData,
    isLoading: questionsLoading,
    error: questionsError,
  } = useQuery({
    queryKey: ['getQuestionsByQuestionnaire', checkInQuestionnaire?.questionnaireId],
    queryFn: fetchQuestionsByQuestionnaire,
    enabled: !!(parsedCompany?.id && checkInQuestionnaire && checkInQuestionnaire?.questionnaireId),
  });

  const questions = useMemo(() => {
    if (questionsData?.success && questionsData?.responseObject) {
      return questionsData?.responseObject;
    }

    return null;
  }, [questionsData, questionnairesData]);

  const {
    data: referenceActionsData,
    isLoading: referenceActionsDataLoading,
    error: referenceActionsDataError,
  } = useUserActionsHooks.useGetUserActionsByReference(
    checkInQuestionnaire?.questionnaireId || null,
  );

  const referenceActionsDataForUpdate = useMemo(() => {
    if (referenceActionsData?.success) {
      return referenceActionsData?.responseObject || [];
    }

    return [];
  }, [referenceActionsData, checkInQuestionnaire]);

  const sortedQuestions = useMemo(() => {
    if (questions && questions.questionnairesV2Questions) {
      return [...questions.questionnairesV2Questions].sort((a, b) => a.orderId - b.orderId);
    }

    return [];
  }, [questions]);

  const getSelectedOptionId = (question: any, score: number): number | null => {
    const options =
      question.questionnairesV2QuestionResponseScales.questionnairesV2QuestionResponseScaleOptions;

    const matchingOption = options.find((option: any) => option.score === score);

    return matchingOption ? matchingOption.id : null;
  };

  useEffect(() => {
    if (sortedQuestions.length > 0) {
      const initialRatings = sortedQuestions.reduce((acc, question) => {
        // Get the response scale options array for this specific question
        const scaleOptions =
          question?.questionnairesV2QuestionResponseScales
            ?.questionnairesV2QuestionResponseScaleOptions;

        // TODO: REMOVED INDEXED DEFAULT VALUE AND ADDED MIN VALUE
        const minValue = Math.min(...scaleOptions.map((opt) => opt.score));

        // Safely get the total number of options
        const totalOptions = scaleOptions?.length ?? 0;

        // Calculate half of the options
        const halfOptions = totalOptions / 2;

        // Get the center index with some offset
        const centerIndex = Math.round(halfOptions + halfOptions / 2) - 1;

        // Safely get the default scale score or fall back to 2
        const defaultScaleValue = scaleOptions?.[centerIndex]?.score ?? 2;

        return { ...acc, [question.id]: DEFAULT_CHECK_IN_VALUE };
      }, {});

      setRatings(initialRatings);
    }
  }, [sortedQuestions]);

  // useEffect(() => {
  //   if (formRef.current && sortedQuestions.length > 0) {
  //     const firstSlider = formRef.current.querySelector(`[id^="slider-"]`);

  //     if (firstSlider) {
  //       (firstSlider as HTMLElement).focus();
  //     }
  //   }
  // }, [sortedQuestions.length]);

  const createAssessmentMutation = useMutation({
    mutationFn: (payload: CreateUserAssessment) =>
      assessmentQuestionnaireApiHandler.createUserAssessment(payload),
    onSuccess: async (data) => {
      if (data.success) {
        showToast(ToastMessages.checkIn.success.CHECK_IN_SAVED, undefined, 'success');
        setAnnouncement('Your check-in was saved — great job!');

        // update actions
        if (referenceActionsDataForUpdate && referenceActionsDataForUpdate.length > 0) {
          Promise.all(
            referenceActionsDataForUpdate.map((dt: { id: number }) =>
              updateUserActionMutation.mutateAsync({
                actionId: dt.id,
                payload: {
                  completed: true,
                  dueDate: null,
                  started: null,
                },
              }),
            ),
          );
        }

        if (data?.responseObject?.ewsTriggered) {
          await dispatch(setCreatedUserAssessment(data?.responseObject));

          setTimeout(() => {
            setHelpAndSpeakUpModalOpen(true);
          }, 100);
        } else {
          closeEverything();

          if (handleStep) {
            handleStep();
          }
        }
      } else {
        showToast(ToastMessages.checkIn.error.CHECK_IN_SAVE_UNSUCCESSFUL, undefined, 'error');
      }

      setIsSubmitting(false);
    },
    onError: (error) => {
      console.error('Check-in submission error:', error);
      setIsSubmitting(false);
      showToast(ToastMessages.checkIn.error.CHECK_IN_SAVE_UNSUCCESSFUL, undefined, 'error');
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  });

  const handleSubmit = () => {
    if (!checkInQuestionnaire?.questionnaireId) {
      showToast(ToastMessages.checkIn.error.QUESTIONNAIRE_NOT_FOUND, undefined, 'error');

      return;
    }

    setIsSubmitting(true);

    // Build user responses array
    const userResponses: UserResponse[] = [];

    Object.entries(ratings).forEach(([questionIdStr, score]) => {
      const questionId = parseInt(questionIdStr, 10);
      const question = sortedQuestions.find((q) => q.id === questionId);

      if (!question) return;

      const selectedOptionId = getSelectedOptionId(question, score);

      if (selectedOptionId === null) {
        showToast(
          `Invalid score ${score} for question: ${question.questionText}`,
          undefined,
          'error',
        );

        return;
      }

      userResponses.push({
        questionId,
        selectedOptionId,
        score,
      });
    });

    // Validate that we have responses for all questions
    if (userResponses.length !== sortedQuestions.length) {
      showToast(ToastMessages.checkIn.error.ANSWER_ALL_QUESTIONS, undefined, 'error');

      return;
    }

    // Create the payload
    const assessmentPayload: CreateUserAssessment = {
      questionnaireId: checkInQuestionnaire.questionnaireId,
      userResponses,
    };

    // Submit the assessment
    createAssessmentMutation.mutate(assessmentPayload);
  };

  const updateModalCache = () => {
    if (popupDataStr) {
      const shortStarCacheHistory = JSON.parse(popupDataStr);

      shortStarCacheHistory.popupClosedToday = true;

      const updatedPopupDataStr = JSON.stringify(shortStarCacheHistory);

      localStorage.setItem('shortStarCacheHistory', updatedPopupDataStr);
    }
  };

  const closeEverything = () => {
    updateModalCache();

    dispatch(resetQuestionnaire());

    if (handleModalOpen) {
      handleModalOpen(false);
    }

    if (refetchCheckInAssessments) {
      refetchCheckInAssessments();
    }

    setSpeakUpVisible(false);
    setHelpAndSpeakUpModalOpen(false);
    setChangedDetected(false);
  };

  const handleHelpAndSpeakUpSkip = () => {
    // Reset the HelpAndSpeakUp modal state
    setSpeakUpVisible(false);
    setHelpAndSpeakUpModalOpen(false);

    closeEverything();

    if (refetchCheckInAssessments) {
      refetchCheckInAssessments();
    }

    if (handleStep) {
      handleStep();
    }
  };

  if ((questionnairesError || questionsError) && (questionnairesLoading || questionsLoading)) {
    showToast(ToastMessages.common.SOMETHING_WENT_MISSING_TRY_AGAIN, undefined, 'error');

    closeEverything();
  }

  const handleRatingChange = (questionId: number, value: number, questionLabel: string) => {
    setRatings((prev) => ({
      ...prev,
      [questionId]: value,
    }));

    // Only set changeDetected to true if the value is not 0
    if (value !== 0) {
      setChangedDetected(true);
    }

    setAnnouncement(`${questionLabel} value set to ${value}`);
  };

  return (
    <div className={`${styles.overlay} ${speakUpVisible || showExperts ? styles.helpVisible : ''}`}>
      {modalOpen && (
        <Modal
          open={modalOpen}
          setOpen={closeEverything}
          className={`${styles.modalRoot} ${
            speakUpVisible || showExperts ? styles.helpVisible : ''
          }`}
        >
          <div className={styles.btnWrapper}>
            <div className={styles.btnClose}>
              <button type="button" onClick={closeEverything}>
                <span>Close</span>&nbsp;
                <i className={classNames('icon', 'icon-x', styles.btnCloseIcon)} />
              </button>
            </div>
          </div>

          <CheckInContent
            questionnairesLoading={questionnairesLoading}
            questionsLoading={questionsLoading}
            sortedQuestions={sortedQuestions}
            ratings={ratings}
            onRatingChange={handleRatingChange}
            onSubmit={handleSubmit}
            onClose={closeEverything}
            announcement={announcement}
            isSubmitting={isSubmitting}
            sliderChangeDetected={changeDetected}
          />
        </Modal>
      )}

      {helpAndSpeakUpModalOpen && (
        <Modal
          open={helpAndSpeakUpModalOpen}
          setOpen={() => setHelpAndSpeakUpModalOpen(false)}
          className={`${styles.modalRootHelp} ${
            speakUpVisible || showExperts ? styles.helpVisible : ''
          }`}
        >
          <HelpAndSpeakUp
            speakUpVisible={showSpeakUp}
            previewInstantHelp={speakUpVisible}
            handleInstantPreview={setSpeakUpVisible}
            handleSkip={handleHelpAndSpeakUpSkip}
            skipButtonName="Skip and close"
            closeButtonEnabled
            setShowExperts={setShowExperts}
            showExperts={showExperts}
          />
        </Modal>
      )}
    </div>
  );
};

CheckIn.defaultProps = {
  modalOpen: false,
  handleModalOpen: () => undefined,
  refetchCheckInAssessments: () => undefined,
};

interface CheckInProps {
  handleStep: () => void;
  modalOpen?: boolean;
  handleModalOpen?: (arg: boolean) => void;
  refetchCheckInAssessments?: () => void;
}
